import appFeatures from 'effective-features!';
import _ from 'lodash';


const metadataDrivenUI = appFeatures.metadataDrivenUI;
const defaultPAS = 'insuranceSuite';

class MetadataDrivenUtil {

    constructor(segment) {
        this.segment = segment || appFeatures.policyAdminSystem;
    }

    /**
     * Check if LOB and step page has metadata driven ui enabled
     * @param {string} lob
     * @param {string} stepPage
     * @returns {boolean}
     * @example
     * return isStepEnabled('ho', 'ho-your-home')
     */
    static isStepEnabled(lob, stepPage) {
        return this.isEnabled() &&
            metadataDrivenUI[lob] &&
            metadataDrivenUI[lob].includes(stepPage);
    }
    /**
     * Check if the metadatada driven ui is enabled
     * @returns {boolean}
     */
    static isEnabled() {
        return metadataDrivenUI && metadataDrivenUI.enabled;
    }

    /**
     * Get from features.json what PAS (Policy Admin System) has been used
     * @returns {string}
     */
    static getPAS() {
        if (this.isEnabled()) {
            return appFeatures.policyAdminSystem || defaultPAS;
        }
    }

    /**
     * Get the default input values set on presentation metadata
     * @param {Object} presentationMetadata
     * @param {Object} skeletonObject
     * @param {string} stringPath
     * @returns {object}
     */
    static getDefaultValues(presentationMetadata, {skeletonObject, stringPath}) {
        let inputsValues = {};

        presentationMetadata.forEach((metadata) => {
            metadata.inputs.forEach((input) => {
                const defaultValue = _.get(input, 'type.properties.default');
                if (defaultValue !== undefined) {
                    if (input.path) {
                        inputsValues = this.combineValues(inputsValues, this.getSkeletonValue(input.path, defaultValue));
                    } else {
                        let skeletonObjectWithAdditionalValue = {
                            [input.viewModelName]: defaultValue
                        };
                        skeletonObjectWithAdditionalValue = _.set(skeletonObject, stringPath, skeletonObjectWithAdditionalValue);
                        inputsValues = this.combineValues(inputsValues, skeletonObjectWithAdditionalValue);
                    }
                }
            });
        });

        return inputsValues;
    }

    /**
     * Merge presentation metadata default values with viewModel pre-filled values
     * @param {object} presentationDefaultValues
     * @param {object} viewModelValue
     * @returns {object}
     */
    static combineValues(presentationDefaultValues, viewModelValue) {
        return _.merge({}, presentationDefaultValues, viewModelValue);
    }

    /**
     * Generate viewModel value skeleton object and apply value to the given path
     * @example
     * const example = _getSkeletonValue('baseData.policyAddress.city', 'Dublin');
     * console.log(example) // { baseData: { policyAddress: { city: 'Dublin' } }
     *
     * @param {string} path
     * @param {*} defaultValue
     * @returns {object}
     */
    static getSkeletonValue(path, defaultValue) {
        return path.split('.').reverse().reduce((accumulatedNode, nodePath) => {
            const node = {};
            if (typeof accumulatedNode === 'string') {
                node[nodePath] = {};
                node[nodePath][accumulatedNode] = defaultValue;
            } else {
                node[nodePath] = accumulatedNode;
            }
            return node;
        });
    }

    /**
     * Recursive method to generate viewModel value skeleton object and stringPath
     * @param {VMNode} vmNode
     * @param {object} skeletonObject
     * @param {string} stringPath
     * @returns {object} {{skeletonObject, stringPath}}
     * @private
     */
    static getParentStructure(vmNode, skeletonObject, stringPath) {
        const vmParentNode = vmNode._parent;
        const hasParentNode = vmParentNode._propertyName || false;

        if (hasParentNode) {
            stringPath = `${vmParentNode._propertyName}.${stringPath}`;
            const node = {
                [vmParentNode._propertyName]: skeletonObject
            };
            return this.getParentStructure(vmParentNode, node, stringPath);
        }
        return {skeletonObject, stringPath};
    }

    /**
     * Generate viewModel skeleton object and string path based on view model node
     * @param {VMNode} vmNode
     * @returns {object} {{skeletonObject, stringPath}}
     *
     * @example
     * const example = getSkeletonAndPathFromVMNode(viewModelNode);
     * console.log(example)
     * // skeletonObject: {{ baseData: { policyAddress: {} }, stringPath: baseData.policyAddress
    */
    static getSkeletonAndPathFromVMNode(vmNode) {

        // returns empty skeletonObject and stringPath when using base view model node
        if (!vmNode._propertyName) {
            return {
                skeletonObject: {},
                stringPath: ''
            };
        }

        const skeletonObject = {
            [vmNode._propertyName]: {}
        };
        const stringPath = vmNode._propertyName;
        return this.getParentStructure(vmNode, skeletonObject, stringPath);
    }

    /**
     * Check if it is the default PAS
     * @returns {boolean}
     */
    static isDefaultPAS() {
        return this.getPAS() === defaultPAS || !this.isEnabled();
    }
}

export default MetadataDrivenUtil;

